import React, { useState, useEffect, useCallback } from 'react';

export type NetworkRequest = {
  id: number;
  url: string;
  method: string;
  status?: number;
  statusText?: string;
  headers?: Record<string, string>;
  requestBody?: string | null;
  responseBody?: string | null;
  duration?: number;
  error?: string;
  timestamp: number;
  pending: boolean;
};

type NetworkPanelProps = {
  className?: string;
};

export const NetworkPanel = React.memo(function NetworkPanel({ className }: NetworkPanelProps) {
  const [requests, setRequests] = useState<NetworkRequest[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<NetworkRequest | null>(null);
  const [filter, setFilter] = useState<'all' | 'fetch' | 'xhr' | 'error'>('all');

  // Listen for network messages from preview
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!event.data || event.data.type !== 'preview-network') return;

      const { event: netEvent, id, url, method, status, statusText, headers, body, duration, error, timestamp } =
        event.data;

      if (netEvent === 'request') {
        setRequests((prev) => [
          ...prev,
          {
            id,
            url,
            method,
            requestBody: body,
            timestamp,
            pending: true,
          },
        ]);
      } else if (netEvent === 'response') {
        setRequests((prev) =>
          prev.map((req) =>
            req.id === id
              ? {
                  ...req,
                  status,
                  statusText,
                  headers,
                  responseBody: body,
                  duration,
                  pending: false,
                }
              : req
          )
        );
      } else if (netEvent === 'error') {
        setRequests((prev) =>
          prev.map((req) =>
            req.id === id
              ? {
                  ...req,
                  error,
                  duration,
                  pending: false,
                }
              : req
          )
        );
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const clearRequests = useCallback(() => {
    setRequests([]);
    setSelectedRequest(null);
  }, []);

  // Filter requests
  const filteredRequests = requests.filter((req) => {
    if (filter === 'error') return req.error || (req.status && req.status >= 400);
    return true;
  });

  const getStatusColor = (req: NetworkRequest) => {
    if (req.pending) return 'pending';
    if (req.error) return 'error';
    if (!req.status) return 'unknown';
    if (req.status >= 500) return 'error';
    if (req.status >= 400) return 'warning';
    if (req.status >= 300) return 'redirect';
    return 'success';
  };

  const formatUrl = (url: string) => {
    try {
      const parsed = new URL(url);
      return parsed.pathname + parsed.search;
    } catch {
      return url;
    }
  };

  return (
    <div className={`network-panel ${className || ''}`}>
      <div className="network-toolbar">
        <div className="network-filters">
          {(['all', 'error'] as const).map((f) => (
            <button
              key={f}
              className={`pill small ${filter === f ? 'success' : 'subtle'}`}
              onClick={() => setFilter(f)}
            >
              {f}
            </button>
          ))}
        </div>
        <button className="micro subtle" onClick={clearRequests}>
          Clear
        </button>
      </div>

      <div className="network-list">
        {filteredRequests.length === 0 ? (
          <p className="muted network-empty">No network requests yet</p>
        ) : (
          filteredRequests.map((req) => (
            <div
              key={req.id}
              className={`network-item ${getStatusColor(req)} ${selectedRequest?.id === req.id ? 'selected' : ''}`}
              onClick={() => setSelectedRequest(req)}
            >
              <span className={`network-method ${req.method.toLowerCase()}`}>{req.method}</span>
              <span className="network-url" title={req.url}>
                {formatUrl(req.url)}
              </span>
              <span className={`network-status ${getStatusColor(req)}`}>
                {req.pending ? '...' : req.error ? 'ERR' : req.status || '-'}
              </span>
              <span className="network-duration">
                {req.duration !== undefined ? `${req.duration}ms` : '-'}
              </span>
            </div>
          ))
        )}
      </div>

      {selectedRequest && (
        <div className="network-details">
          <div className="network-details-header">
            <span className={`network-method ${selectedRequest.method.toLowerCase()}`}>
              {selectedRequest.method}
            </span>
            <span className="network-details-url">{selectedRequest.url}</span>
            <button className="network-close" onClick={() => setSelectedRequest(null)}>
              ×
            </button>
          </div>

          <div className="network-details-content">
            <div className="network-detail-section">
              <h4>General</h4>
              <div className="network-detail-row">
                <span className="network-detail-label">Status:</span>
                <span className={`network-detail-value ${getStatusColor(selectedRequest)}`}>
                  {selectedRequest.pending
                    ? 'Pending...'
                    : selectedRequest.error
                      ? `Error: ${selectedRequest.error}`
                      : `${selectedRequest.status} ${selectedRequest.statusText || ''}`}
                </span>
              </div>
              <div className="network-detail-row">
                <span className="network-detail-label">Duration:</span>
                <span className="network-detail-value">
                  {selectedRequest.duration !== undefined ? `${selectedRequest.duration}ms` : 'N/A'}
                </span>
              </div>
            </div>

            {selectedRequest.headers && Object.keys(selectedRequest.headers).length > 0 && (
              <div className="network-detail-section">
                <h4>Response Headers</h4>
                {Object.entries(selectedRequest.headers).map(([key, value]) => (
                  <div key={key} className="network-detail-row">
                    <span className="network-detail-label">{key}:</span>
                    <span className="network-detail-value">{value}</span>
                  </div>
                ))}
              </div>
            )}

            {selectedRequest.requestBody && (
              <div className="network-detail-section">
                <h4>Request Body</h4>
                <pre className="network-body">{selectedRequest.requestBody}</pre>
              </div>
            )}

            {selectedRequest.responseBody && (
              <div className="network-detail-section">
                <h4>Response Body</h4>
                <pre className="network-body">{selectedRequest.responseBody}</pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

export default NetworkPanel;
